"use client";

import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import { onAuthStateChanged } from "firebase/auth";
import { ThemeProvider } from "next-themes";
import { useEffect, useRef, useState } from "react";

import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { markSignedIn, markSignedOut } from "@/lib/auth/session-cookie";
import { clearCustomerSessions } from "@/lib/booking/customer-session";
import { firebaseAuth } from "@/lib/firebase";
import { clearSessions } from "@/lib/pb/session";
import { stopRealtime } from "@/lib/realtime/live";
import { RealtimeUpdates } from "@/providers/realtime";
import { useAuthStore } from "@/stores/auth-store";

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Keeps the auth store, and the middleware's marker cookie, in sync with
 * Firebase's session.
 *
 * The cookie is refreshed on every sign-in event rather than only at login, so
 * a session that Firebase restores after its cookie expired still gets one.
 */
function AuthListener() {
  const setUser = useAuthStore((state) => state.setUser);

  useEffect(
    () =>
      onAuthStateChanged(firebaseAuth, (user) => {
        setUser(user);
        void (user ? markSignedIn() : markSignedOut());

        if (user) {
          // Filling Firebase's token cache while the business is still being
          // resolved: signing in to MainApp wants a token, and minting one
          // takes a moment.
          void user.getIdToken();
          return;
        }

        // Both PocketBase sessions outlive Firebase's by a fortnight, so
        // losing the Firebase one has to take them with it — otherwise the
        // dashboard keeps working for a signed-out account.
        stopRealtime();
        clearSessions();
        // The booking pages hold their own pair of sessions, and they outlive
        // Firebase's by the same fortnight.
        clearCustomerSessions();
      }),
    [setUser],
  );

  return null;
}

/**
 * Queries answered by MainApp. They describe the account rather than a
 * business, so they outlive a switch.
 *
 * The tenant session is deliberately not one of them: it is keyed by business
 * and cached for minutes, so switching back to one visited a moment ago would
 * hand back its profile — and let the dashboard through — while the client is
 * still pointed at, and holding a token for, the business in between.
 */
const DIRECTORY_QUERIES = new Set(["tenants", "invitations"]);

/**
 * Throws away the previous business's data when another one is chosen.
 *
 * Query keys name a collection, not a business — "customers" means whichever
 * server the client happens to be pointed at. Without this, switching business
 * shows the last one's rows until every screen happens to refetch.
 */
function TenantScope() {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((state) => state.tenant?.id ?? null);
  const previous = useRef(tenantId);

  // Done while rendering rather than in an effect: react-query answers from the
  // cache during render, so a screen mounting in the same commit as the switch
  // would otherwise paint the previous business's rows first.
  if (previous.current !== tenantId) {
    previous.current = tenantId;

    // Reset, not remove: removing a query that a mounted screen is watching
    // leaves that screen holding the data it already had, with nothing left in
    // the cache to refetch it. Reset clears it and refetches what is on screen.
    queryClient.resetQueries({
      predicate: (query) => !DIRECTORY_QUERIES.has(String(query.queryKey[0])),
    });
  }

  return null;
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(makeQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <TooltipProvider>
          <AuthListener />
          {/* Both inside the query client: one empties it, the other refills it. */}
          <TenantScope />
          <RealtimeUpdates />
          {children}
          <Toaster />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
